import { Component, OnInit } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';

/**
 * CONSULTAS → UTILIDAD (UTL-17; Mario 1-nov). Lo que entró a las cuentas CT, lo que se retornó a los comercios en los
 * cierres y lo que le queda a CrediTotal (comisión + uso + fees). Filtros: atajos de rango, desde–hasta, comercio y cuenta
 * receptora. El gráfico es un SVG armado aquí (por hora si el núcleo manda `por_hora`, rango ≤ 7 días; si no, por día):
 * la plantilla solo recorre `barras`, `ejeY` y `ejeX`. Bs primero y USD pequeño; sin tasa el Bs es «—» (nunca un 0 inventado).
 */
const DIA_MS = 86400000;
const X0 = 56, X1 = 1228, BASE = 240, ALTO = 210;   // área de dibujo del SVG (viewBox 1240 × 280)
const SEP = 2;                                      // px entre cuotas (abajo) y fees (arriba)

type Atajo = 'hoy' | 'ayer' | '7dias' | 'mes';

@Component({
  selector: 'app-liquidacion-utilidad',
  templateUrl: './liquidacion-utilidad.component.html'
})
export class LiquidacionUtilidadComponent implements OnInit {
  f = { desde: '', hasta: '', comercio: '', cuenta: '' };
  r: any = null;
  cuentas: { id: string; nombre: string }[] = [];
  comercios: string[] = [];
  cargando = false;
  error = '';
  porHora = false;
  barras: any[] = [];
  ejeY: { y: number; texto: string }[] = [];
  ejeX: { x: number; texto: string }[] = [];
  pico: { x: number; y: number; texto: string } | null = null;
  conceptos: any[] = [];

  constructor(private liq: LiquidacionService) { }

  ngOnInit(): void { this.atajo('hoy'); }

  // ── fechas (día de Caracas, AAAA-MM-DD) ──
  private hoyCaracas = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(new Date());
  private suma = (dia: string, n: number): string => new Date(Date.parse(`${dia}T00:00:00Z`) + n * DIA_MS).toISOString().slice(0, 10);
  private rango = (a: Atajo): { desde: string; hasta: string } => {
    const hoy = this.hoyCaracas();
    if (a === 'ayer') { const y = this.suma(hoy, -1); return { desde: y, hasta: y }; }
    if (a === '7dias') return { desde: this.suma(hoy, -6), hasta: hoy };
    if (a === 'mes') return { desde: `${hoy.slice(0, 8)}01`, hasta: hoy };
    return { desde: hoy, hasta: hoy };
  };

  /** Atajo activo según el rango puesto (vacío si el rango es otro). */
  get atajoActivo(): Atajo | '' {
    for (const a of ['hoy', 'ayer', '7dias', 'mes'] as Atajo[]) { const g = this.rango(a); if (g.desde === this.f.desde && g.hasta === this.f.hasta) return a; }
    return '';
  }

  atajo = (a: Atajo): void => { const g = this.rango(a); this.f.desde = g.desde; this.f.hasta = g.hasta; void this.buscar(); };

  /** Un clic en la fila de una cuenta filtra toda la vista por ella (otro clic en la misma la suelta). */
  elegirCuenta = (id: string): void => { this.f.cuenta = this.f.cuenta === id ? '' : id; void this.buscar(); };

  private pedido = 0;

  buscar = async (): Promise<void> => {
    const n = ++this.pedido;
    this.cargando = true; this.error = '';
    try {
      const r = await this.liq.utilidad({ desde: this.f.desde, hasta: this.f.hasta, comercio: this.f.comercio, cuenta: this.f.cuenta });
      if (n !== this.pedido) return;   // una respuesta vieja que llega tarde se descarta
      this.r = r;
      if (!this.f.cuenta && Array.isArray(r.cuentas)) this.cuentas = r.cuentas;
      if (!this.f.comercio && Array.isArray(r.comercios)) this.comercios = r.comercios;
      this.armar();
    } catch (e: any) { if (n === this.pedido) { this.error = e?.error?.msg || 'No se pudo leer la utilidad.'; this.r = null; } }
    finally { if (n === this.pedido) this.cargando = false; }
  };

  // ── formato: Bs primero, «—» sin tasa ──
  private num = (v: number, d = 2): string => Number(v).toLocaleString('es', { minimumFractionDigits: d, maximumFractionDigits: d });
  fb = (bs: number | null | undefined): string => (bs === null || bs === undefined ? '—' : `Bs ${this.num(bs)}`);
  fu = (usd: number | null | undefined): string => `$ ${this.num(Number(usd) || 0)}`;
  /** Bs de una cifra en USD a la tasa promedio del rango; null si no hay tasa. */
  bsT = (usd: number): number | null => (this.r?.tasa_promedio ? Math.round((Number(usd) || 0) * this.r.tasa_promedio * 100) / 100 : null);
  /** Bs de una parte de una fila (recibido real de la fila × proporción); sin eso, la tasa promedio. */
  bsFila = (fila: any, usd: number): number | null => (fila && Number(fila.usd) > 0 && fila.bs !== null && fila.bs !== undefined
    ? Math.round((Number(usd) || 0) * (fila.bs / fila.usd) * 100) / 100 : this.bsT(usd));
  pct = (parte: number, todo: number): number => (Number(todo) > 0 ? Math.round((Number(parte) || 0) / Number(todo) * 1000) / 10 : 0);
  margen = (d: any): number => this.pct(d.utilidad_usd, d.usd);
  nombreCuenta = (id: string): string => this.cuentas.find(c => c.id === id)?.nombre || id;

  // ── cuadre y barra «A dónde fue» ──
  get cuadra(): boolean {
    const s = this.r?.resumen; if (!s) return true;
    return Math.abs((s.recibido?.usd || 0) - ((s.a_cuotas?.usd || 0) + (s.fees?.usd || 0) + (s.sin_aplicar?.usd || 0))) < 0.005;
  }
  get destino(): { texto: string; pct: number; clase: string }[] {
    const s = this.r?.resumen; if (!s) return [];
    const t = s.recibido?.usd || 0;
    return [
      { texto: 'A cuotas', pct: this.pct(s.a_cuotas?.usd, t), clase: 'bg-[#404040]' },
      { texto: 'Fees', pct: this.pct(s.fees?.usd, t), clase: 'bg-[#16a34a]' },
      { texto: 'Sin aplicar', pct: this.pct(s.sin_aplicar?.usd, t), clase: 'bg-[#d4d4d4]' },
    ];
  }
  get hayRecibido(): boolean { return (this.r?.resumen?.recibido?.n || 0) > 0 || (this.r?.resumen?.recibido?.usd || 0) > 0; }

  // ── utilidad por concepto: comisión, uso y cada fee ──
  private armarConceptos(): void {
    const l = this.r?.resumen?.liquidado || {};
    const filas = [
      { nombre: 'Comisión de los cierres', n: l.lineas, usd: l.comision_usd || 0, bs: l.comision_bs },
      { nombre: 'Uso de plataforma', n: l.lineas, usd: l.uso_usd || 0, bs: l.uso_bs },
      ...(this.r?.por_concepto || []).map((c: any) => ({ nombre: c.nombre || c.fee, n: c.n, usd: c.usd, bs: this.bsT(c.usd) })),
    ];
    const total = filas.reduce((a, c) => a + (Number(c.usd) || 0), 0);
    this.conceptos = filas.map(c => ({ ...c, bs: c.bs === undefined ? this.bsT(c.usd) : c.bs, peso: this.pct(c.usd, total) }));
  }

  // ── gráfico: apiladas, cuotas abajo y fees arriba ──
  private escala(max: number): { paso: number; tope: number } {
    if (!(max > 0)) return { paso: 1, tope: 1 };
    const e = Math.pow(10, Math.floor(Math.log10(max / 4)));
    const paso = [1, 2, 2.5, 5, 10].map(m => m * e).find(p => max / p <= 4) || 10 * e;
    return { paso, tope: Math.ceil(max / paso) * paso };
  }
  private etiquetaEje(v: number, enBs: boolean): string {
    if (!enBs) return this.num(v, 0);
    return `${(v / 1e6).toLocaleString('es', { maximumFractionDigits: 2 })} M`;
  }

  private armar(): void {
    const r = this.r; this.armarConceptos();
    this.porHora = Array.isArray(r?.por_hora);
    const enBs = !!r?.tasa_promedio || (r?.por_dia || []).some((d: any) => d.bs !== null && d.bs !== undefined);
    // la serie completa del rango, con huecos en cero, para que se vea la tendencia y no solo los días con pagos
    const dias: string[] = [];
    for (let d = r.desde; d && r.hasta && d <= r.hasta && dias.length < 400; d = this.suma(d, 1)) dias.push(d);
    const filas: any[] = [];
    if (this.porHora) {
      const mapa = new Map<string, any>((r.por_hora || []).map((x: any) => [`${x.dia}|${x.h}`, x] as [string, any]));
      for (const dia of dias) for (let h = 0; h < 24; h++) filas.push({ dia, h, etiqueta: `${dia} ${String(h).padStart(2, '0')}:00`, f: mapa.get(`${dia}|${h}`) });
    } else {
      const mapa = new Map<string, any>((r.por_dia || []).map((x: any) => [x.dia, x] as [string, any]));
      for (const dia of dias) filas.push({ dia, h: 0, etiqueta: dia, f: mapa.get(dia) });
    }
    const val = (f: any, usd: number): number => (!f ? 0 : enBs ? (this.bsFila(f, usd) || 0) : (Number(usd) || 0));
    const datos = filas.map(x => ({
      ...x,
      total: val(x.f, x.f?.usd), cuotas: val(x.f, x.f?.cuotas_usd), fees: val(x.f, x.f?.fees_usd),
    }));
    const max = Math.max(0, ...datos.map(d => d.total));
    const { paso, tope } = this.escala(max);
    const k = ALTO / tope;
    const n = datos.length || 1, slot = (X1 - X0) / n, bw = Math.max(1.5, slot * 0.72);
    this.barras = datos.map((d, i) => {
      const x = X0 + i * slot + (slot - bw) / 2;
      const hF = d.fees > 0 ? Math.max(1, d.fees * k) : 0;
      const hC = d.cuotas > 0 ? Math.max(1, d.cuotas * k) : 0;
      const sep = hC > 0 && hF > 0 ? SEP : 0;
      const f = d.f;
      const titulo = f
        ? `${d.etiqueta} · ${enBs ? this.fb(this.bsFila(f, f.usd)) : '—'} · ${this.fu(f.usd)} · ${f.n} pagos · fees ${this.fu(f.fees_usd)}`
        : `${d.etiqueta} · sin pagos`;
      return { x, w: bw, xt: X0 + i * slot, wt: slot, yC: BASE - hC, hC, yF: BASE - hC - sep - hF, hF, titulo };
    });
    this.ejeY = [];
    for (let v = 0; v <= tope + paso / 1000; v += paso) this.ejeY.push({ y: BASE - v * k, texto: this.etiquetaEje(v, enBs) });
    // eje X: por hora, el día en la hora 0 y «12h»; por día, unas 12 marcas
    this.ejeX = [];
    if (this.porHora) {
      datos.forEach((d, i) => { if (d.h === 0 || d.h === 12) this.ejeX.push({ x: X0 + (i + 0.5) * slot, texto: d.h === 0 ? d.dia.slice(5).split('-').reverse().join('-') : '12h' }); });
    } else {
      const cada = Math.max(1, Math.ceil(datos.length / 12));
      datos.forEach((d, i) => { if (i % cada === 0) this.ejeX.push({ x: X0 + (i + 0.5) * slot, texto: d.dia.slice(5).split('-').reverse().join('-') }); });
    }
    // la etiqueta es solo del pico
    const ip = datos.findIndex(d => d.total === max);
    this.pico = max > 0 && ip >= 0
      ? { x: Math.min(Math.max(X0 + (ip + 0.5) * slot, X0 + 40), X1 - 40), y: BASE - max * k - 6, texto: `pico ${enBs ? this.fb(max) : this.fu(max)} · ${datos[ip].etiqueta}` }
      : null;
  }
}
